import type { Event } from '@casetrace/shared';

export interface EventAttachment {
  externalId?: string;
  sourceItemId?: string;
  fileName?: string;
  contentType?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function toEventAttachment(value: unknown): EventAttachment | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const attachment: EventAttachment = {};
  const externalId = stringValue(value.externalId);
  const sourceItemId = stringValue(value.sourceItemId);
  const fileName = stringValue(value.fileName ?? value.filename);
  const contentType = stringValue(value.contentType);
  if (externalId) {
    attachment.externalId = externalId;
  }
  if (sourceItemId) {
    attachment.sourceItemId = sourceItemId;
  }
  if (fileName) {
    attachment.fileName = fileName;
  }
  if (contentType) {
    attachment.contentType = contentType;
  }
  return attachment.externalId || attachment.sourceItemId || attachment.fileName
    ? attachment
    : undefined;
}

function metadataAttachmentFallback(
  event: Pick<Event, 'metadata' | 'sourceItemId'>,
): EventAttachment | undefined {
  const fileName = stringValue(
    event.metadata.fileName ?? event.metadata.filename ?? event.metadata.filnavn,
  );
  if (!fileName) {
    return undefined;
  }
  const contentType = stringValue(event.metadata.contentType ?? event.metadata.filtype);
  return {
    fileName,
    contentType,
    sourceItemId: event.sourceItemId,
  };
}

const FILE_EXTENSION =
  'pdf|docx?|xlsx?|pptx?|csv|zip|rar|7z|jpe?g|png|gif|webp|heic|heif|txt|mp3|m4a|mp4|mov';

function normalizePreview(preview: string): string {
  return preview.replace(/\u200e/g, '').trim();
}

function fileNameFromPageCountCaption(preview: string): string | undefined {
  const match = preview.match(
    new RegExp(`^(.+\\.(?:${FILE_EXTENSION}))\\s*[•·]\\s*\\d+\\s*(?:sider|pages)\\b`, 'i'),
  );
  const fileName = match?.[1]?.trim();
  return fileName ? fileName : undefined;
}

function fileNameFromSimpleToken(preview: string): string | undefined {
  const match = preview.match(new RegExp(`([A-Za-z0-9._-]+\\.(?:${FILE_EXTENSION}))\\b`, 'i'));
  return match?.[1];
}

export function attachmentFromContentPreview(
  preview: string | undefined,
): EventAttachment | undefined {
  if (!preview) {
    return undefined;
  }
  const text = normalizePreview(preview);
  if (!text) {
    return undefined;
  }

  const fromCaption = fileNameFromPageCountCaption(text);
  if (fromCaption) {
    return { fileName: fromCaption };
  }

  const startsWithAttachment = text.toLowerCase().startsWith('vedlegg:');
  const body = startsWithAttachment ? text.slice('vedlegg:'.length).trim() : text;
  const tokenName = fileNameFromSimpleToken(body);
  if (tokenName) {
    return { fileName: tokenName };
  }
  if (startsWithAttachment && body) {
    return { fileName: body };
  }
  return text.toLowerCase().includes('vedlegg') && body ? { fileName: body } : undefined;
}

type EventAttachmentSource = Pick<Event, 'type' | 'metadata' | 'sourceItemId' | 'contentPreview'>;

function synthesizedCountAttachments(event: EventAttachmentSource): EventAttachment[] {
  if (event.type !== 'imessage' && event.type !== 'sms') {
    return [];
  }
  const count = Number(event.metadata.attachmentCount ?? 0);
  if (count <= 0 && event.metadata.hasAttachments !== true) {
    return [];
  }
  const n = count > 0 ? count : 1;
  return Array.from({ length: n }, (_, index) => ({
    fileName: n === 1 ? 'Vedlegg' : `Vedlegg ${index + 1}`,
  }));
}

export function eventAttachments(event: EventAttachmentSource): EventAttachment[] {
  const value = event.metadata.attachments;
  if (Array.isArray(value)) {
    const attachments = value.flatMap((item) => {
      const attachment = toEventAttachment(item);
      return attachment ? [attachment] : [];
    });
    if (attachments.length > 0) {
      return attachments;
    }
  }

  const single =
    metadataAttachmentFallback(event) ?? attachmentFromContentPreview(event.contentPreview);
  if (single) {
    return [single];
  }
  return synthesizedCountAttachments(event);
}

export function eventHasAttachments(event: EventAttachmentSource): boolean {
  return (
    eventAttachments(event).length > 0 ||
    event.metadata.hasAttachments === true ||
    Number(event.metadata.attachmentCount ?? 0) > 0
  );
}

export function attachmentLabel(attachments: EventAttachment[]): string {
  if (attachments.length === 0) {
    return '';
  }
  if (attachments.length === 1) {
    return attachments[0]?.fileName ?? 'Vedlegg';
  }
  return `${attachments.length} vedlegg`;
}

function previewLooksLikeAttachmentCaption(
  preview: string,
  attachments: EventAttachment[],
): boolean {
  if (preview.toLowerCase().startsWith('vedlegg:')) {
    return true;
  }
  if (fileNameFromPageCountCaption(preview)) {
    return true;
  }
  if (attachments.length !== 1) {
    return false;
  }
  const fileName = attachments[0]?.fileName?.trim().toLowerCase();
  if (!fileName) {
    return false;
  }
  const text = preview.toLowerCase();
  return text === fileName || text.startsWith(`${fileName} `) || text.startsWith(`${fileName}•`);
}

export function isAttachmentOnlyPreview(
  preview: string | undefined,
  attachments: EventAttachment[],
): boolean {
  if (!preview || attachments.length === 0) {
    return false;
  }
  return previewLooksLikeAttachmentCaption(normalizePreview(preview), attachments);
}

export function attachmentLineLabel(
  attachments: EventAttachment[],
  preview: string | undefined,
): string {
  const fallback = attachmentLabel(attachments);
  if (!preview || !isAttachmentOnlyPreview(preview, attachments)) {
    return fallback;
  }
  const text = normalizePreview(preview);
  if (text.toLowerCase().startsWith('vedlegg:')) {
    return text.slice('vedlegg:'.length).trim() || fallback;
  }
  return text;
}

export function isSelfNamedDocument(
  event: Pick<Event, 'type' | 'title'>,
  attachments: EventAttachment[],
): boolean {
  if (event.type !== 'document' || attachments.length !== 1) {
    return false;
  }
  const fileName = attachments[0]?.fileName?.trim().toLowerCase();
  return Boolean(fileName) && fileName === event.title.trim().toLowerCase();
}
